// The How to play explainer as a timeline. Each event changes the scene at a
// set moment (and may play a sound); replaying events from the start rebuilds
// the scene at any time, which is how seeking and chapter skips work.
//
// The board is a cut-down Fortress board: 9 columns × 13 rows, your pink base
// at the bottom, their blue base at the top, gems and Pow squares.

export const STAGE_W = 720;
export const STAGE_H = 450;
export const ROWS = 13;
export const COLS = 9;
export const CELL = 28;
export const PITCH = 30;
export const BOARD_X = 30;
export const BOARD_Y = 30;

export const MY_BASE = { r0: 10, c0: 3 }; // 3×3, flag in the middle
export const OPP_BASE = { r0: 0, c0: 3 };
// Hammer (Pow) squares as on the real Fortress board: green in the corners,
// grey on the middle row.
export const POW = { "0,0": "green", "0,8": "green", "12,0": "green", "12,8": "green", "6,0": "grey", "6,8": "grey" };
export const GEMS = ["6,6", "8,1"];

export const HAND_X = 340;
export const HAND_Y = 318;
export const HAND_TILE = 38;
export const HAND_STEP = 44;
export const BACK_BTN = { x: 340, y: 372, w: 56, h: 40 };
export const GREEN_BTN = { x: 404, y: 372, w: 80, h: 40 };
export const GREY_BTN = { x: 492, y: 372, w: 80, h: 40 };
export const PLAY_BTN = { x: 580, y: 372, w: 106, h: 40 };

export const SHIELD = "SHIELD";
export const CONNECTOR = "CONNECTOR";

export const cellCenter = (r, c) => ({ x: BOARD_X + c * PITCH + CELL / 2, y: BOARD_Y + r * PITCH + CELL / 2 });
export const handCenter = (i) => ({ x: HAND_X + i * HAND_STEP + HAND_TILE / 2, y: HAND_Y + HAND_TILE / 2 });
const btnCenter = (b) => ({ x: b.x + b.w / 2, y: b.y + b.h / 2 });

export const inBase = (b, r, c) => r >= b.r0 && r < b.r0 + 3 && c >= b.c0 && c < b.c0 + 3;

// The squares touching your base from outside: where a first word can start.
export const START_CELLS = (() => {
  const out = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (inBase(MY_BASE, r, c)) continue;
      if ([[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]].some(([a, b]) => inBase(MY_BASE, a, b))) out.push(`${r},${c}`);
    }
  }
  return out;
})();

export const UP = [-1, 0];
const LEFT = [0, -1];
const RIGHT = [0, 1];
const DOWN = [1, 0];
const CLOCKWISE = [[-1, 0], [0, 1], [1, 0], [0, -1]];

let uid = 0;
const handOf = (letters) => letters.map((l) => ({ id: `h${uid++}`, l }));

export function initialState() {
  return {
    overlay: "title",
    caption: null,
    tiles: {},
    ghosts: [],
    anchor: null,
    dir: null,
    placed: [],
    showStarts: false,
    startWave: 0,
    goal: false,
    gemPulse: false,
    powPulse: false,
    shieldPulse: false,
    connPulse: false,
    gemFly: null, // a collected gem flying from the board to the hand
    hole: null, // the smashed gap, highlighted before you build through it
    gems: GEMS.slice(),
    green: 0,
    grey: 0,
    greyLocked: true,
    unlockAt: null,
    lockShake: null,
    cardReady: null, // "green" | "grey": the card just earned, pulsing
    cutMode: false, // "green" | "grey" while picking a tile to smash
    hammers: null, // the green vs grey explainer card, with its start time
    boom: null,
    oppTry: null,
    hand: handOf(["R", "A", "I", "D", "E", "N", "O", "T"]),
    handWave: 0,
    finger: { x: 640, y: 500, show: false, tap: 0 },
    pressed: null, // "play" | "back" | "green" | "grey" | hand tile id, with pressedAt
    pressedAt: 0,
    chip: null,
    toast: null,
    raider: null,
    flagTaken: false,
    confetti: false,
  };
}

// The cell the dashed box sits on: where the next letter goes, unless that
// is the solid anchor itself, in which case the square after it. This
// mirrors kbNextCell() and the board render in public/game/jangle.html.
export function nextCell(s) {
  if (!s.anchor || !s.dir) return null;
  const [dr, dc] = s.dir;
  let r;
  let c;
  if (s.placed.length) {
    const last = s.placed[s.placed.length - 1];
    r = last.r + dr;
    c = last.c + dc;
  } else if (!s.tiles[`${s.anchor.r},${s.anchor.c}`]) {
    return { r: s.anchor.r + dr, c: s.anchor.c + dc };
  } else {
    r = s.anchor.r + dr;
    c = s.anchor.c + dc;
  }
  // Typing flows past your own tiles but stops at theirs and at wall tiles.
  for (let tile = s.tiles[`${r},${c}`]; tile; tile = s.tiles[`${r},${c}`]) {
    if (tile.who !== "me" || tile.l === SHIELD) return null;
    r += dr;
    c += dc;
  }
  return { r, c };
}

// Where the next typed letter lands (the anchor itself while it is empty).
function landingCell(s) {
  if (!s.placed.length && !s.tiles[`${s.anchor.r},${s.anchor.c}`]) return { ...s.anchor };
  return nextCell(s);
}

function buildTimeline() {
  const ev = [];
  const chapters = [];
  const at = (t, apply, sound) => ev.push({ t, apply, sound });
  const chapter = (t, title) => chapters.push({ t, title });
  const caption = (t, step, title, text) => at(t, (s) => { s.caption = { step, title, text }; });
  const text = (t, body) => at(t, (s) => { s.caption = { ...s.caption, text: body }; });

  const fingerTo = (t, p) => at(t, (s) => { s.finger = { ...s.finger, x: p.x, y: p.y, show: true }; });
  const fingerHide = (t) => at(t, (s) => { s.finger = { ...s.finger, show: false }; });
  const tap = (t, fn, sound = "tap") => at(t, (s) => { s.finger = { ...s.finger, tap: t }; fn(s, t); }, sound);

  // Glide to a square and tap it: it becomes the solid anchor, pointing up.
  const tapCell = (t, r, c, dir = UP) => {
    fingerTo(t, cellCenter(r, c));
    tap(t + 500, (s) => {
      s.anchor = { r, c };
      s.dir = dir;
      s.placed = [];
      s.chip = null;
    });
  };
  // Tap the anchor again: turn clockwise, skipping a direction whose next
  // square is off the board or already has a tile (as the game does).
  const turnCell = (t) =>
    tap(t, (s) => {
      const i = CLOCKWISE.findIndex(([dr, dc]) => dr === s.dir[0] && dc === s.dir[1]);
      for (let n = 1; n <= 4; n++) {
        const d = CLOCKWISE[(i + n) % 4];
        const r = s.anchor.r + d[0];
        const c = s.anchor.c + d[1];
        if (r < 0 || r >= ROWS || c < 0 || c >= COLS || s.tiles[`${r},${c}`]) continue;
        s.dir = d;
        return;
      }
    }, "turn");

  // Tap a tile in the hand: it flies onto the board where the box says.
  const typeLetter = (t, letter, { move = 450, sound = "place" } = {}) => {
    at(t, (s) => {
      const i = s.hand.findIndex((h) => h.l === letter);
      s.finger = { ...s.finger, ...handCenter(i), show: true };
    });
    tap(t + move, (s) => {
      const i = s.hand.findIndex((h) => h.l === letter);
      const from = handCenter(i);
      s.pressed = s.hand[i].id;
      s.pressedAt = t + move;
      s.hand.splice(i, 1);
      const cell = landingCell(s);
      s.tiles[`${cell.r},${cell.c}`] = { l: letter, who: "me", locked: false, st: null, from, born: t + move };
      s.placed.push(cell);
    }, sound);
  };

  const markWord = (t, cells, st, chipText) =>
    at(t, (s) => {
      cells.forEach((k) => { if (s.tiles[k]) s.tiles[k].st = st; });
      s.chip = { text: chipText, st, t };
    }, st === "good" ? "good" : "bad");

  const pressPlay = (t, extra) => {
    fingerTo(t, btnCenter(PLAY_BTN));
    tap(t + 500, (s) => {
      s.pressed = "play";
      s.pressedAt = t + 500;
      Object.values(s.tiles).forEach((tile) => { if (tile.who === "me") { tile.locked = true; tile.st = null; } });
      s.anchor = null;
      s.dir = null;
      s.placed = [];
      s.chip = null;
      if (extra) extra(s);
    }, "lock");
  };

  const refill = (t, letters) =>
    at(t, (s) => { s.hand = s.hand.concat(handOf(letters)); s.handWave = t; }, "draw");

  const toast = (t, msg, sound, kind = "good") => at(t, (s) => { s.toast = { text: msg, t, kind }; }, sound);
  const clearToast = (t) => at(t, (s) => { s.toast = null; });

  const oppTile = (t, k, l) => at(t, (s) => { s.tiles[k] = { l, who: "opp", locked: true, st: null, born: t }; }, "opp");
  const myTile = (t, k, l) => at(t, (s) => { s.tiles[k] = { l, who: "me", locked: true, st: null, born: t }; }, "place");

  // A faint example word, typed out from a start square.
  const ghostWord = (t, r, c, dir, letters) => {
    tapCell(t, r, c, dir);
    letters.split("").forEach((l, i) =>
      at(t + 800 + i * 230, (s) => {
        s.ghosts = s.ghosts.concat([{ r: r + dir[0] * i, c: c + dir[1] * i, l, born: t + 800 + i * 230 }]);
      }, "place"));
  };

  // ── 0 · Title ──
  chapter(0, "Intro");
  at(300, () => {}, "jingle");

  // ── 1 · The goal ──
  let t = 3400;
  chapter(t, "The goal");
  at(t, (s) => { s.overlay = null; s.goal = true; });
  caption(t, "The goal", "Race to their base", "Your base is pink, at the bottom. Theirs is blue, at the top. Get one of your tiles into their base to grab their flag.");

  // ── 2 · Where to start ──
  t = 9800;
  chapter(t, "Where to start");
  at(t, (s, now) => { s.goal = false; s.showStarts = true; s.startWave = now; });
  START_CELLS.forEach((_, i) => at(t + i * 110, () => {}, "turn"));
  caption(t, "Start a word", "9 starting squares", "Your first word can start on any of the 9 squares around your base.");

  // ── 3 · Any direction: one word each way, from a start square ──
  t = 12800;
  chapter(t, "Any direction");
  caption(t, "Start a word", "Up, down, left or right", "Tap a square and type outwards. A word can go up, down, left or right.");
  [
    [9, 4, UP, "CAT", "↑ CAT"],
    [11, 6, RIGHT, "SUN", "→ SUN"],
    [10, 2, DOWN, "DOG", "↓ DOG"],
    [11, 2, LEFT, "OWL", "← OWL"],
  ].forEach(([r, c, dir, word, label], i) => {
    const tw = t + 300 + i * 2300;
    ghostWord(tw, r, c, dir, word);
    at(tw + 1400, (s, now) => { s.chip = { text: label, st: "good", t: now }; }, "good");
    at(tw + 2200, (s) => { s.ghosts = []; s.chip = null; s.anchor = null; s.dir = null; });
  });

  // ── 4 · Type ──
  t = 22400;
  chapter(t, "Type a word");
  caption(t, "Type a word", "Tap a square, then letters", "Tap a starting square. Your first letter goes in the solid box, and the dashed box shows which way you'll type.");
  tapCell(t + 200, 9, 4);
  typeLetter(t + 2600, "R", { move: 600 });
  text(t + 3200, "Tap letters in your hand. Each one drops onto the board and the box moves along.");
  typeLetter(t + 3800, "A");
  typeLetter(t + 4600, "I");
  typeLetter(t + 5400, "D");
  at(t + 6200, (s) => { s.showStarts = false; });
  markWord(t + 6200, ["9,4", "8,4", "7,4", "6,4"], "good", "RAID ✓");
  text(t + 6500, "Every line of your tiles must spell a real word. Words can read either way.");

  // ── 5 · Play ──
  t = 32200;
  chapter(t, "Play");
  caption(t, "Play", "Tap Play", "Happy with your word? Tap Play to lock your tiles in and draw new letters.");
  pressPlay(t + 400);
  refill(t + 1500, ["A", "L", "K", "S"]);
  text(t + 2800, "Then it's their turn.");
  fingerHide(t + 2800);
  oppTile(t + 3400, "1,2", "F");
  oppTile(t + 3850, "1,1", "O");
  oppTile(t + 4300, "1,0", "E");
  toast(t + 5000, "Your turn!", "turn2");
  clearToast(t + 6300);

  // ── 6 · Build and turn, out in the open ──
  t = 39000;
  chapter(t, "Turn the direction");
  caption(t, "Build and turn", "Tap one of your tiles", "Tap a tile you've already played to type on from it. The dashed box points up.");
  tapCell(t + 300, 6, 4);
  caption(t + 2200, "Build and turn", "Tap again to turn", "Each tap turns the dashed box clockwise.");
  turnCell(t + 2700);
  text(t + 3700, "Squares that already have a tile are skipped.");
  turnCell(t + 4000);
  turnCell(t + 5300);
  text(t + 5600, "Keep tapping until it points where you want to go.");
  turnCell(t + 6500);

  // ── 7 · Type, fix a red word ──
  t = 46600;
  chapter(t, "Fix a word");
  caption(t, "Build and turn", "Type your next word", "Now each letter goes to the right.");
  typeLetter(t + 100, "E", { move: 550 });
  typeLetter(t + 1000, "A");
  typeLetter(t + 1800, "L");
  typeLetter(t + 2600, "K");
  markWord(t + 3500, ["6,4", "6,5", "6,6", "6,7", "6,8"], "bad", "DEALK ✗");
  caption(t + 3700, "Fix a word", "Red means not a word", "Tap ⌫ to take the last letter back.");
  fingerTo(t + 4800, btnCenter(BACK_BTN));
  tap(t + 5300, (s, now) => {
    const last = s.placed.pop();
    const k = `${last.r},${last.c}`;
    s.hand = s.hand.concat(handOf([s.tiles[k].l]));
    delete s.tiles[k];
    Object.values(s.tiles).forEach((tile) => { if (tile.st === "bad") tile.st = null; });
    s.pressed = "back";
    s.pressedAt = now;
    s.chip = null;
  }, "back");
  typeLetter(t + 5900, "T", { move: 550 });
  markWord(t + 6900, ["6,4", "6,5", "6,6", "6,7", "6,8"], "good", "DEALT ✓");

  // ── 8 · A gem gives a wall tile, a hammer square gives a hammer ──
  t = 55000;
  chapter(t, "Gems and hammers");
  caption(t, "Gems", "A gem gives you a wall tile", "Your A landed on a gem. When your word is real, you collect the gem…");
  at(t, (s) => { s.gemPulse = true; });
  fingerHide(t);
  at(t + 2000, (s, now) => {
    s.gemPulse = false;
    s.gems = s.gems.filter((g) => g !== "6,6");
    s.gemFly = { t: now, from: cellCenter(6, 6), to: handCenter(s.hand.length) };
  }, "gem");
  at(t + 2800, (s, now) => {
    s.gemFly = null;
    s.hand = s.hand.concat([{ id: `h${uid++}`, l: SHIELD }]);
    s.handWave = now;
    s.shieldPulse = true;
  }, "shield");
  toast(t + 2800, "Gem → +1 Wall tile");
  text(t + 3000, "…and it turns into a wall tile in your hand. Every gem you collect gives you one.");
  clearToast(t + 5000);
  caption(t + 5000, "Hammers", "Hammer squares", "Your T is on a grey hammer square, so you also get a grey hammer.");
  at(t + 5000, (s) => { s.shieldPulse = false; s.powPulse = true; });
  at(t + 6300, (s) => { s.powPulse = false; s.grey = 1; s.cardReady = "grey"; }, "pow");
  toast(t + 6300, "+1 Grey hammer");
  clearToast(t + 7600);
  text(t + 7600, "But a grey hammer is locked until you've used a green one.");
  fingerTo(t + 8000, btnCenter(GREY_BTN));
  tap(t + 8500, (s, now) => { s.pressed = "grey"; s.pressedAt = now; s.lockShake = now; s.cardReady = null; }, "bad");
  toast(t + 8500, "Locked!", null, "bad");
  clearToast(t + 10000);
  pressPlay(t + 10200);
  refill(t + 11200, ["R", "I", "M"]);
  fingerHide(t + 11700);
  text(t + 11700, "Then it's their turn again. Their word is heading your way.");
  oppTile(t + 12300, "2,1", "N");
  oppTile(t + 12750, "3,1", "E");

  // ── 9 · Green vs grey hammers ──
  t = 68500;
  chapter(t, "Green vs grey");
  caption(t, "Hammers", "Green vs grey hammers", "Green hammers work straight away. Grey hammers stay locked until you've used a green one. Each one smashes one of their tiles.");
  at(t, (s, now) => { s.hammers = { t: now, step: 1 }; }, "pow");
  at(t + 1500, (s) => { s.hammers = { ...s.hammers, step: 2 }; }, "pow");
  at(t + 4200, (s) => { s.hammers = { ...s.hammers, step: 3 }; }, "draw");
  at(t + 9000, (s) => { s.hammers = null; });

  // ── 10 · Wall tile ──
  t = 78000;
  chapter(t, "Wall tiles");
  caption(t, "Wall tiles", "Build a wall", "Tap any square, then your wall tile, to block it.");
  at(t, (s) => { s.shieldPulse = true; });
  tapCell(t + 300, 4, 1);
  typeLetter(t + 1600, SHIELD, { move: 550, sound: "shield" });
  at(t + 2200, (s) => { s.shieldPulse = false; });
  text(t + 2600, "Nobody can type onto it or past it, and hammers can't smash it.");
  pressPlay(t + 3600);
  refill(t + 4500, [CONNECTOR]);
  fingerHide(t + 4800);
  at(t + 5600, (s, now) => { s.oppTry = { r: 4, c: 1, t: now }; }, "bad");
  toast(t + 5600, "Blocked!", null, "bad");
  at(t + 6800, (s) => { s.oppTry = null; s.toast = null; });
  text(t + 6800, "They have to go around it.");
  [["3,2", "G"], ["3,3", "G"], ["3,4", "N"], ["3,5", "O"], ["3,6", "G"]].forEach(([k, l], i) => oppTile(t + 7300 + i * 450, k, l));
  text(t + 9800, "Their word EGGNOG now stands between you and their base.");

  // ── 11 · Connector tile ──
  t = 89500;
  chapter(t, "Connector tiles");
  caption(t, "Connector tiles", "Branch off with a Connector", "You drew a Connector tile. It isn't a letter: the words on each side of it stay separate.");
  at(t, (s) => { s.connPulse = true; });
  tapCell(t + 1800, 6, 3);
  text(t + 2600, "A letter here would have to make a word with DEALT. A Connector doesn't.");
  typeLetter(t + 3600, CONNECTOR, { move: 550 });
  at(t + 4200, (s) => { s.connPulse = false; });
  typeLetter(t + 5000, "N");
  typeLetter(t + 5800, "O");
  markWord(t + 6700, ["6,4", "6,5", "6,6", "6,7", "6,8", "5,3", "4,3"], "good", "NO ✓ · DEALT ✓");
  text(t + 7000, "So you can start a new word right beside an old one. Place it touching your own tiles.");
  pressPlay(t + 9200);
  fingerHide(t + 10000);

  // ── 12 · Using hammers ──
  t = 100500;
  chapter(t, "Using hammers");
  caption(t, "Hammers", "Get a green hammer", "A few turns later, your word PRESENT reaches the green square in the corner.");
  [["5,8", "N"], ["4,8", "E"], ["3,8", "S"], ["2,8", "E"], ["1,8", "R"], ["0,8", "P"]].forEach(([k, l], i) => myTile(t + 300 + i * 280, k, l));
  markWord(t + 2300, ["0,8", "1,8", "2,8", "3,8", "4,8", "5,8", "6,8"], "good", "PRESENT ✓");
  at(t + 2900, (s) => { s.green = 1; s.cardReady = "green"; }, "pow");
  toast(t + 2900, "+1 Green hammer");
  at(t + 4000, (s) => {
    Object.values(s.tiles).forEach((tile) => { tile.st = null; });
    s.chip = null;
    s.toast = null;
  });
  caption(t + 4000, "Hammers", "Smash a hole", "EGGNOG is in your way. Tap your green hammer, then one of their tiles.");
  fingerTo(t + 4200, btnCenter(GREEN_BTN));
  tap(t + 4700, (s, now) => { s.pressed = "green"; s.pressedAt = now; s.cutMode = "green"; s.cardReady = null; });
  fingerTo(t + 5500, cellCenter(3, 5));
  tap(t + 6100, (s, now) => {
    delete s.tiles["3,5"];
    s.boom = { r: 3, c: 5, t: now };
    s.green = 0;
    s.cutMode = false;
  }, "boom");
  caption(t + 7200, "Hammers", "Grey unlocked", "Using a green hammer unlocks your grey one.");
  at(t + 7700, (s, now) => { s.greyLocked = false; s.unlockAt = now; s.boom = null; s.cardReady = "grey"; }, "gem");
  toast(t + 7700, "Grey hammer unlocked!");
  clearToast(t + 9000);
  text(t + 9000, "Now the grey hammer works too. Make the hole wider.");
  fingerTo(t + 9000, btnCenter(GREY_BTN));
  tap(t + 9500, (s, now) => { s.pressed = "grey"; s.pressedAt = now; s.cutMode = "grey"; s.cardReady = null; });
  fingerTo(t + 10300, cellCenter(3, 4));
  tap(t + 10900, (s, now) => {
    delete s.tiles["3,4"];
    s.boom = { r: 3, c: 4, t: now };
    s.grey = 0;
    s.cutMode = false;
  }, "boom");
  fingerHide(t + 11900);
  at(t + 11900, (s) => { s.boom = null; });

  // ── 13 · Through the hole into their base ──
  t = 113000;
  chapter(t, "Through the hole");
  at(t, (s, now) => { s.hand = handOf(["N", "O", "T", "S", "R", "I", "M", "K"]); s.handWave = now; }, "draw");
  caption(t, "Win", "Build through the hole", "Now type a word through the gap and into their base.");
  at(t, (s, now) => { s.hole = { r: 3, c: 5, t: now }; });
  tapCell(t + 300, 6, 5);
  at(t + 1500, (s) => { s.hole = null; });
  typeLetter(t + 1600, "N");
  typeLetter(t + 2400, "O");
  typeLetter(t + 3200, "T");
  typeLetter(t + 4000, "S");
  markWord(t + 4900, ["2,5", "3,5", "4,5", "5,5", "6,5"], "good", "STONE ✓");
  text(t + 5200, "Your S is inside their base. Tap Play!");
  pressPlay(t + 5800);
  fingerHide(t + 6700);

  // ── Raider runs the path and takes the flag ──
  t = 120500;
  caption(t, "Win", "Grab their flag!", "Your raider runs along your tiles and takes their flag. You win!");
  const path = [[10, 4], [9, 4], [8, 4], [7, 4], [6, 4], [6, 5], [5, 5], [4, 5], [3, 5], [2, 5], [1, 5], [1, 4]];
  at(t, (s) => { s.raider = { r: 11, c: 4, pose: "walk", flag: null, face: 1 }; });
  path.forEach(([r, c], i) =>
    at(t + 400 + i * 230, (s) => {
      const face = c < s.raider.c ? -1 : c > s.raider.c ? 1 : s.raider.face;
      s.raider = { ...s.raider, r, c, face };
    }, "step"));
  const grab = t + 400 + path.length * 230 + 100;
  at(grab, (s) => { s.raider = { ...s.raider, pose: "grab" }; });
  at(grab + 450, (s) => { s.flagTaken = true; s.raider = { ...s.raider, pose: "cheer", flag: "#4FB0F0" }; s.confetti = true; }, "win");
  at(grab + 2600, (s) => { s.overlay = "end"; });

  const duration = grab + 2600 + 400;
  ev.sort((a, b) => a.t - b.t);
  return { events: ev, chapters, duration };
}

export const TIMELINE = buildTimeline();

// The scene at time `ms`, built by replaying every event up to it.
export function stateAt(ms) {
  uid = 0;
  const s = initialState();
  for (const e of TIMELINE.events) {
    if (e.t > ms) break;
    e.apply(s, e.t);
  }
  return s;
}
